import { useCallback } from 'react';
import { useRecoilValue } from 'recoil';
import { useAuthSession } from '~/contexts/auth/AuthSessionContext';
import { useActiveEditTarget } from '~/hooks/target/useActiveEditTarget';
import { DICOMApprovalStatus } from '~/domain/dicom/dicomConstants';
import {
    ROI_POINT_TYPES,
    RoiIdentificationCodeSequence,
    RoiInterpretedType,
} from '~/domain/dicom/dicomstructureset';
import {
    domainEventBus,
    RoiApprovalStatusChangeRequestedEvent,
    RoiContourThicknessChangeRequestedEvent,
    RoiCopyRequestedEvent,
    RoiElectronDensityOverrideChangeRequestedEvent,
    RoiMassDensityOverrideChangeRequestedEvent,
    RoiDescriptionChangeRequestedEvent,
    RoiInterpretedTypeChangeRequestedEvent,
    RoiLockChangeRequestedEvent,
    RoiNameChangeRequestedEvent,
    RoiPointCoordinatesChangeRequestedEvent,
    RoiSdfCopyOverRequestedEvent,
    RoiSelectionRequestedEvent,
    RoiVisibilityChangeRequestedEvent,
} from '~/domain/events';
import { RoiReadModel } from '~/domain/readmodels/StructureSetReadModel';
import type { Grid } from '~/engine/data/common/Grid';
import { localization } from '~/i18n';
import { sessionDataAtom } from '~/state/atoms/sessionAtoms';
import { enqueueSnackbar } from '~/utils/notifications';

/**
 * How far inside the image edge a point held there sits, mm: below the 0.1 mm
 * the coordinates are shown to, above the float32 rounding of a position.
 */
export const POINT_EDGE_INSET_MM = 0.01;

type PointCoordinatesMm = { x: number; y: number; z: number };

/**
 * `coordinates` (mm), or, past the edge of the image `grid` describes, the
 * nearest position in it, with the user told. Unchanged with no image.
 */
export function holdPointInsideImage(
    grid: Grid | undefined,
    coordinates: PointCoordinatesMm
): PointCoordinatesMm {
    const { x, y, z } = coordinates;
    if (!grid || grid.includesPoint([x, y, z])) {
        return coordinates;
    }
    enqueueSnackbar(localization.t('poi.coordinates.heldInsideImage'), {
        variant: 'warning',
    });
    const [heldX, heldY, heldZ] = grid.clampPoint(
        [x, y, z],
        POINT_EDGE_INSET_MM
    );
    return { x: heldX, y: heldY, z: heldZ };
}

export const formatIdentificationCode = (
    code: RoiIdentificationCodeSequence | undefined
) => {
    return code
        ? `${code.codeValue} ${code.codingSchemeDesignator} ${code.codeMeaning}`
        : '';
};

/**
 * Hook for managing ROI operations via domain events.
 *
 * Provides functions to modify ROI properties by publishing domain events
 * to the event bus. All operations are handled asynchronously through the event-driven architecture.
 * The actual state updates are performed by event handlers, maintaining separation of concerns.
 *
 * @param roiInstance The ROI instance to operate on
 * @returns ROI domain event publishing functions.
 */
const useRoi = (roiInstance: RoiReadModel) => {
    const { userName } = useAuthSession();
    const sessionData = useRecoilValue(sessionDataAtom);
    // Resolved through the edit target: the grid Environment that shows the
    // active set is the one that receives the strokes, so its volume and
    // emptiness are the ones the clinician sees.
    const { environment: env } = useActiveEditTarget();

    const getRoiVolumeCm3 = useCallback(() => {
        return env?.getRoiVolumeCm3(roiInstance.roiNumber) ?? Number.NaN;
    }, [env, roiInstance.roiNumber]);

    const isRoiEmpty = useCallback(() => {
        return env?.isRoiEmpty(roiInstance.roiNumber) ?? true;
    }, [env, roiInstance.roiNumber]);

    const getRoiEquivalentSphereDiameterCm = useCallback(() => {
        return 2 * Math.cbrt((3 * getRoiVolumeCm3()) / (4 * Math.PI));
    }, [getRoiVolumeCm3]);

    const setVisible = useCallback(
        async (visible: boolean) => {
            try {
                await domainEventBus.publish(
                    new RoiVisibilityChangeRequestedEvent(
                        roiInstance.roiNumber,
                        visible,
                        'user'
                    )
                );
            } catch (error) {
                console.error(`Error updating ROI visibility: ${error}`);
            }
        },
        [roiInstance.roiNumber]
    );

    const setLocked = useCallback(
        async (locked: boolean) => {
            try {
                await domainEventBus.publish(
                    new RoiLockChangeRequestedEvent(
                        roiInstance.roiNumber,
                        locked,
                        'user'
                    )
                );
            } catch {
                console.error('Error updating ROI lock');
            }
        },
        [roiInstance.roiNumber]
    );

    const setName = useCallback(
        async (newName: string) => {
            try {
                await domainEventBus.publish(
                    new RoiNameChangeRequestedEvent(
                        roiInstance.roiNumber,
                        newName,
                        'user'
                    )
                );
            } catch (error) {
                console.error(`Error updating ROI name: ${error}`);
            }
        },
        [roiInstance.roiNumber]
    );

    const setContourThickness = useCallback(
        async (newThickness: number) => {
            try {
                await domainEventBus.publish(
                    new RoiContourThicknessChangeRequestedEvent(
                        roiInstance.roiNumber,
                        newThickness,
                        'user'
                    )
                );
            } catch (error) {
                console.error(`Error updating ROI contour thickness: ${error}`);
            }
        },
        [roiInstance.roiNumber]
    );

    const setRoiApprovalStatus = useCallback(
        async (newStatus: DICOMApprovalStatus, newReviewerName?: string) => {
            const reviewer =
                newReviewerName ??
                sessionData?.username ??
                userName ??
                'unknown';

            try {
                await domainEventBus.publish(
                    new RoiApprovalStatusChangeRequestedEvent(
                        roiInstance.roiNumber,
                        newStatus,
                        reviewer,
                        'user'
                    )
                );
            } catch (error) {
                console.error(`Error updating ROI approval status: ${error}`);
            }
        },
        [roiInstance.roiNumber, sessionData, userName]
    );

    const setPointCoordinates = useCallback(
        async (coordinates: PointCoordinatesMm) => {
            try {
                await domainEventBus.publish(
                    new RoiPointCoordinatesChangeRequestedEvent(
                        roiInstance.roiNumber,
                        holdPointInsideImage(env?.image?.grid, coordinates),
                        'user'
                    )
                );
            } catch (error) {
                console.error(`Error updating ROI point coordinates: ${error}`);
            }
        },
        [env, roiInstance.roiNumber]
    );

    const setInterpretedType = useCallback(
        async (newType: RoiInterpretedType) => {
            try {
                await domainEventBus.publish(
                    new RoiInterpretedTypeChangeRequestedEvent(
                        roiInstance.roiNumber,
                        newType,
                        'user'
                    )
                );

                // When switching from a volumetric type to a point type,
                // auto-place the point at the current crosshair position so
                // the user never has to manually set an initial coordinate.
                const wasPointType = ROI_POINT_TYPES.includes(
                    roiInstance.interpretedType
                );
                const isNowPointType = ROI_POINT_TYPES.includes(newType);
                if (!wasPointType && isNowPointType) {
                    const coords = env?.planeIntersectionPoint.coordinates;
                    if (coords) {
                        await setPointCoordinates({
                            x: coords[0],
                            y: coords[1],
                            z: coords[2],
                        });
                    }
                }
            } catch (error) {
                console.error(`Error updating ROI interpreted type: ${error}`);
            }
        },
        [
            roiInstance.roiNumber,
            roiInstance.interpretedType,
            env,
            setPointCoordinates,
        ]
    );

    const setElectronDensityOverride = useCallback(
        async (newDensity: number | undefined) => {
            try {
                await domainEventBus.publish(
                    new RoiElectronDensityOverrideChangeRequestedEvent(
                        roiInstance.roiNumber,
                        newDensity,
                        'user'
                    )
                );
            } catch (error) {
                console.error(
                    `Error updating ROI electron density override: ${error}`
                );
            }
        },
        [roiInstance.roiNumber]
    );

    const setMassDensityOverride = useCallback(
        async (newDensity: number | undefined) => {
            try {
                await domainEventBus.publish(
                    new RoiMassDensityOverrideChangeRequestedEvent(
                        roiInstance.roiNumber,
                        newDensity,
                        'user'
                    )
                );
            } catch (error) {
                console.error(
                    `Error updating ROI mass density override: ${error}`
                );
            }
        },
        [roiInstance.roiNumber]
    );

    const setRoiDescription = useCallback(
        async (newDescription: string) => {
            try {
                await domainEventBus.publish(
                    new RoiDescriptionChangeRequestedEvent(
                        roiInstance.roiNumber,
                        newDescription,
                        'user'
                    )
                );
            } catch (error) {
                console.error(`Error updating ROI description: ${error}`);
            }
        },
        [roiInstance.roiNumber]
    );

    const setRoiSelected = useCallback(async () => {
        try {
            await domainEventBus.publish(
                new RoiSelectionRequestedEvent(roiInstance.roiNumber, 'user')
            );
        } catch (error) {
            console.error(`Error selecting ROI: ${error}`);
        }
    }, [roiInstance.roiNumber]);

    const copyRoi = useCallback(
        async (structureSetUid: string) => {
            try {
                await domainEventBus.publish(
                    new RoiCopyRequestedEvent(
                        structureSetUid,
                        roiInstance.roiNumber,
                        'user'
                    )
                );
            } catch (error) {
                console.error(`Error copying ROI: ${error}`);
            }
        },
        [roiInstance.roiNumber]
    );

    const copySdfOver = useCallback(
        async (structureSetUid: string, targetRoiNumber: number) => {
            try {
                await domainEventBus.publish(
                    new RoiSdfCopyOverRequestedEvent(
                        structureSetUid,
                        roiInstance.roiNumber,
                        targetRoiNumber,
                        'user'
                    )
                );
            } catch (error) {
                console.error(`Error copying SDF over: ${error}`);
            }
        },
        [roiInstance.roiNumber]
    );

    return {
        getRoiVolumeCm3,
        isRoiEmpty,
        getRoiEquivalentSphereDiameterCm,
        setVisible,
        setLocked,
        setName,
        setContourThickness,
        setRoiApprovalStatus,
        setInterpretedType,
        setElectronDensityOverride,
        setMassDensityOverride,
        setPointCoordinates,
        setRoiDescription,
        setRoiSelected,
        copyRoi,
        copySdfOver,
    };
};

export default useRoi;
